import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { useAddressSuggestion } from '../api/address-api';
import { reportErrorToConsole } from '../services/error-logger';
// Simple debounce implementation
function debounce(func, delay) {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
}
const AddressAutoCompleteInput = ({ setShowAutoCompleteAddressInput, selectedAddress, setSelectedAddress, onChangeAddress, fullAddressDisplay, financingProgramId, getRecaptchaToken, }) => {
    const { t } = useTranslation('shared');
    const ADDRESS_DROP_DOWN_LIMIT = 5;
    const [addressInput, setAddressInput] = useState(null);
    const [suggestionsList, setSuggestionsList] = useState([]);
    const [currentSuggestionDto, setCurrentSuggestionDto] = useState(null);
    const [focusedItem, setFocusedItem] = useState(null);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [addressSuggestion, isLoadingSuggestion] = useAddressSuggestion(financingProgramId);
    const getFilteredInputLength = (inputValue) => {
        return inputValue.replaceAll(' ', '').length;
    };
    const setAddressInputCallback = useCallback(async (inputValue) => {
        setFocusedItem(null);
        let recaptchaToken;
        if (getRecaptchaToken) {
            recaptchaToken = await getRecaptchaToken(executeRecaptcha, 'submit_addressSuggestions');
        }
        else {
            if (!executeRecaptcha) {
                reportErrorToConsole('ReCAPTCHA non disponible.');
                return;
            }
            recaptchaToken = await executeRecaptcha('submit_addressSuggestions');
        }
        const dto = {
            addressInput: inputValue,
            recaptchaToken,
        };
        if (getFilteredInputLength(inputValue) > 3) {
            const response = await addressSuggestion(dto);
            setCurrentSuggestionDto(response);
            const list = response.suggestions.map((x) => x.addressSuggestion);
            setSuggestionsList(list);
            setFocusedItem(0);
        }
    }, [executeRecaptcha, addressSuggestion, getRecaptchaToken]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedUpdateAddress = useCallback(debounce(setAddressInputCallback, 1000), [setAddressInputCallback]);
    const handleAddressChange = useCallback((e) => {
        const inputValue = e.target.value;
        setSelectedAddress(null);
        setAddressInput(inputValue);
        if (inputValue) {
            debouncedUpdateAddress(inputValue);
        }
        else {
            setAddressInput('');
            setSelectedAddress(null);
            setSuggestionsList([]);
        }
    }, [debouncedUpdateAddress, setSelectedAddress]);
    const handleSuggestionClick = async (suggestion) => {
        setAddressInput(suggestion);
        if (currentSuggestionDto) {
            const selectedSuggestion = currentSuggestionDto.suggestions.find((item) => item.addressSuggestion === suggestion);
            if (selectedSuggestion) {
                const res = {
                    placeId: selectedSuggestion.placeId,
                };
                await onChangeAddress(res);
                setSelectedAddress(res);
            }
        }
        setSuggestionsList([]);
    };
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (focusedItem !== null) {
                handleSuggestionClick(suggestionsList[focusedItem]).catch(reportErrorToConsole);
            }
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedItem((fi) => {
                if (fi !== null && fi < ADDRESS_DROP_DOWN_LIMIT - 1) {
                    return fi + 1;
                }
                return fi;
            });
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedItem((fi) => {
                if (fi !== null && fi > 0) {
                    return fi - 1;
                }
                return fi;
            });
        }
    };
    return (_jsxs("div", { className: "control-group", children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }, children: [_jsx("label", { htmlFor: "fullAddress", children: t('addressAutoComplete.enterAddress') }), _jsxs("button", { className: "btn btn-underline", style: { marginLeft: 'auto', fontSize: '85%' }, onClick: () => setShowAutoCompleteAddressInput(false), children: [t('addressAutoComplete.cantFind'), _jsx("span", { className: "help-tip-exclamation", style: { marginLeft: '1px' }, children: _jsx("p", { children: t('addressAutoComplete.cantFindToolTip') }) })] })] }), _jsxs("div", { className: "input-with-icon", children: [selectedAddress !== null && !isLoadingSuggestion && _jsx("i", { className: "fa fa-check icon" }), isLoadingSuggestion && _jsx("i", { className: "fa fa-spinner fa-spin icon", style: { top: '35%' } }), _jsx("input", { type: "text", id: "fullAddress", name: "fullAddress", maxLength: 100, "aria-label": "Address search input", value: addressInput === '' ? addressInput : addressInput || fullAddressDisplay, onChange: handleAddressChange, autoComplete: "off", placeholder: t('addressAutoComplete.placeHolder'), onKeyDown: handleKeyPress })] }), _jsx("div", { children: suggestionsList.length > 0 && (_jsxs("div", { className: "control-group center", style: { padding: '0' }, children: [_jsx("ul", { className: "merchant-list", style: { cursor: 'pointer' }, children: suggestionsList.slice(0, ADDRESS_DROP_DOWN_LIMIT).map((suggestion, index) => (_jsx("li", { children: _jsx("button", { type: "button", className: `merchant-list-item ${index === focusedItem ? 'focused' : ''}`, onClick: () => handleSuggestionClick(suggestion), children: _jsx("h3", { className: "merchant-list-item-highlighted", children: suggestion }) }) }, suggestion))) }), selectedAddress === null && suggestionsList.length > ADDRESS_DROP_DOWN_LIMIT && (_jsxs("button", { className: "merchant-list-footer", children: [_jsx("h3", { style: { fontSize: '1.75rem' }, children: t('serviceProvider.serviceProviderSearch.resultsFound', {
                                        merchantsFound: suggestionsList.length > ADDRESS_DROP_DOWN_LIMIT
                                            ? ADDRESS_DROP_DOWN_LIMIT
                                            : suggestionsList.length,
                                    }) }), _jsx("p", { style: { fontSize: '1.5rem', marginTop: '1rem', cursor: 'pointer' }, children: t('addressAutoComplete.refineSearch') })] })), selectedAddress === null &&
                            suggestionsList.length > 0 &&
                            suggestionsList.length <= ADDRESS_DROP_DOWN_LIMIT && (_jsx("button", { className: "merchant-list-footer", children: _jsx("h3", { style: { fontSize: '1.75rem' }, children: t('addressAutoComplete.pickOption') }) }))] })) })] }));
};
export default AddressAutoCompleteInput;
